import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HasAccess } from "@/components/common/has-access";
import { toast } from "@/hooks/use-toast";
import { bundleSchema } from "@/features/bundles/ui/bundle-form";
import { useCreateBundle } from "@/features/bundles/hooks/use-create-bundle";
import { checkSlugAvailability } from "@/features/payment-pages/services/check-slug-availability";
import { buildPaymentUrl } from "@/features/payment-pages/utils/pay-url";
import { useProducts } from "@/features/forms/hooks/use-products";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { useRegionFilter } from "@/features/regions/hooks/use-region-filter";
import { ALL_REGIONS_CODE, getStoredRegion } from "@/features/regions/constants";
import { Region } from "@/features/regions/types/region";
import BundleWizardStepper, {
  WizardStep,
} from "@/features/bundles/ui/wizard/bundle-wizard-stepper";
import StepBasics from "@/features/bundles/ui/wizard/step-basics";
import StepPricing from "@/features/bundles/ui/wizard/step-pricing";
import StepLinks from "@/features/bundles/ui/wizard/step-links";
import StepReview from "@/features/bundles/ui/wizard/step-review";
import RescheduleBundlesStep from "@/features/bundles/ui/wizard/reschedule-bundles-step";
import { BundleWizardData } from "@/features/bundles/ui/wizard/wizard-form";
import { CreateBundleRequest } from "@/pages/bundles/types/bundle";
import { useCreateRescheduleBundles } from "@/features/bundles/hooks/use-create-reschedule-bundles";
import { RescheduleBundleItem } from "@/features/bundles/services/create-reschedule-bundles";

const STEPS: WizardStep[] = [
  { key: "basics", label: "Basics" },
  { key: "pricing", label: "Pricing" },
  { key: "links", label: "Links" },
  { key: "review", label: "Review" },
];

// Reschedule bundles use a different, shorter flow (region on Basics, then one
// fee per managed event).
const RESCHEDULE_STEPS: WizardStep[] = [
  { key: "basics", label: "Basics" },
  { key: "reschedule", label: "Reschedule fees" },
];

// Fields validated when leaving each step (review has no gate — submit validates all).
const STEP_FIELDS: Record<number, (keyof BundleWizardData)[]> = {
  0: ["name", "status"],
  1: ["currency", "amount", "reschedule_amount", "expires_in_day", "expires_in_month"],
};

// Outer: resolve the default region and wait for the region list to load BEFORE
// mounting the form, so the region lands in defaultValues (no async prefill,
// no re-render race). A bundle always needs one concrete region, so when the
// global toggle is "All" we fall back to the last-used region from storage.
export default function BundleCreate() {
  const [searchParams] = useSearchParams();
  const { region: regionSegment } = useParams();
  const productId = searchParams.get("product_id");
  const prefillRegionCode = useRegionFilter();

  const { data: regions, isLoading } = useActiveRegions();
  const { data: products } = useProducts();

  const defaultRegion = useMemo(() => {
    if (!regions) return null;
    const stored = getStoredRegion();
    const code =
      prefillRegionCode ??
      (stored && stored !== ALL_REGIONS_CODE ? stored : undefined);
    return regions.find((r) => r.code === code) ?? null;
  }, [regions, prefillRegionCode]);

  if (isLoading || !regions) {
    return (
      <HasAccess permission="write">
        <div className="max-w-3xl mx-auto p-6 text-sm text-gray-500">
          Loading…
        </div>
      </HasAccess>
    );
  }

  return (
    <BundleCreateWizard
      regionSegment={regionSegment}
      productId={productId}
      productName={products?.find((p) => p.id === productId)?.name}
      defaultRegion={defaultRegion}
    />
  );
}

function BundleCreateWizard({
  regionSegment,
  productId,
  productName,
  defaultRegion,
}: {
  regionSegment?: string;
  productId: string | null;
  productName?: string;
  defaultRegion: Region | null;
}) {
  const navigate = useNavigate();
  const createBundle = useCreateBundle();
  const createReschedule = useCreateRescheduleBundles();

  const standardId = useMemo(() => crypto.randomUUID(), []);
  const [current, setCurrent] = useState(0);
  // Reschedule flow: per-managed-event fees captured by RescheduleBundlesStep.
  const [rescheduleItems, setRescheduleItems] = useState<RescheduleBundleItem[]>(
    [],
  );
  // Wizard-only (not a bundle field): chain into payment-page creation on save.
  // On by default — creating a bundle normally means creating its payment page.
  const [createPaymentPage, setCreatePaymentPage] = useState(true);
  // The payment slug → builds the bundle's payment_page_link + seeds the
  // chained payment-page wizard.
  const [paymentSlug, setPaymentSlug] = useState("");
  const [checkingSlug, setCheckingSlug] = useState(false);

  const form = useForm<BundleWizardData>({
    resolver: zodResolver(bundleSchema),
    defaultValues: {
      standard_id: standardId,
      name: "",
      type: "onboarding",
      payment_timing: "BEFORE_SCHEDULING",
      session_types: [],
      currency: defaultRegion?.currency ?? "INR",
      region: defaultRegion?.code ?? "",
      region_id: defaultRegion?.id ?? null,
      amount: 0,
      reschedule_amount: 0,
      payment_page_link: "",
      session_quantity: null,
      is_expiration_in_days: false,
      expires_in_day: null,
      expires_in_month: null,
      product_id: productId,
      company_id: null,
      total_allowed_user: null,
      user_payment_type: null,
      form_ids: [],
      coupon_ids: [],
      status: "ACTIVE",
      show_to_public: false,
    },
  });

  const bundleType = useWatch({ control: form.control, name: "type" });
  const isReschedule = bundleType === "reschedule";
  const steps = isReschedule ? RESCHEDULE_STEPS : STEPS;
  const stepFields: Record<number, (keyof BundleWizardData)[]> = isReschedule
    ? { 0: ["status"] }
    : STEP_FIELDS;

  const goBackToList = () => navigate(`/${regionSegment ?? "all"}/bundles`);

  const handleRescheduleCreate = async () => {
    const region_id = form.getValues("region_id");
    if (!region_id) {
      toast({
        title: "Region required",
        description: "Pick a region in the Basics step.",
        variant: "destructive",
      });
      return;
    }
    if (!rescheduleItems.length) {
      toast({
        title: "No fees entered",
        description: "Enter a reschedule fee for at least one managed event.",
        variant: "destructive",
      });
      return;
    }
    try {
      const res = await createReschedule.mutateAsync({
        region_id,
        product_id: productId ?? undefined,
        show_to_public: form.getValues("show_to_public"),
        items: rescheduleItems,
      });
      toast({
        title: "Reschedule bundles created",
        description: `${res.created} created, ${res.skipped} skipped (already existed).`,
      });
      goBackToList();
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to create reschedule bundles",
        variant: "destructive",
      });
    }
  };

  const handleNext = async () => {
    if (current === 0 && !form.getValues("region_id")) {
      toast({
        title: "Region required",
        description: "Pick a region to continue.",
        variant: "destructive",
      });
      return;
    }
    // Step 2 (Links): validate either the payment slug (when creating a page)
    // or the raw payment link. (Reschedule flow has no Links step.)
    if (!isReschedule && current === 2) {
      if (createPaymentPage) {
        const slug = paymentSlug.trim();
        if (!slug) {
          toast({
            title: "Payment slug required",
            description: "Enter a slug for the payment page.",
            variant: "destructive",
          });
          return;
        }
        setCheckingSlug(true);
        try {
          const available = await checkSlugAvailability(slug);
          if (!available) {
            toast({
              title: "Slug already exists",
              description: "Choose a different payment slug.",
              variant: "destructive",
            });
            return;
          }
        } catch {
          // Network error — don't hard-block; creation-time checks still guard.
        } finally {
          setCheckingSlug(false);
        }
        form.setValue("payment_page_link", buildPaymentUrl(slug));
      } else {
        const ok = await form.trigger(["payment_page_link"]);
        if (!ok) return;
      }
      setCurrent((c) => Math.min(c + 1, steps.length - 1));
      return;
    }

    const fields = stepFields[current];
    const ok = fields ? await form.trigger(fields) : true;
    if (ok) setCurrent((c) => Math.min(c + 1, steps.length - 1));
  };

  const onSubmit = async (data: BundleWizardData) => {
    // Defense-in-depth: only the explicit Create button on the final step
    // may create — never a stray submit from an earlier step.
    if (current !== steps.length - 1) return;
    try {
      // Fold the card-display fields into the bundle's metadata JSON.
      const { tagline, features, isMostPopular, ...rest } = data;
      const payload = {
        ...rest,
        metadata: {
          tagline: tagline ?? null,
          features: (features ?? []).map((f) => f.trim()).filter(Boolean),
          isMostPopular: !!isMostPopular,
        },
      };
      await createBundle.mutateAsync(payload as CreateBundleRequest);
      toast({ title: "Success", description: "Bundle created successfully" });
      if (createPaymentPage) {
        // Hand off to payment-page creation with this offering preselected.
        // Use the bundle's own region (may differ from the URL segment) so the
        // payment-page's region-scoped offering list includes it.
        const region = data.region || regionSegment || "all";
        const slug = paymentSlug.trim();
        const slugParam = slug ? `&slug=${encodeURIComponent(slug)}` : "";
        navigate(
          `/${region}/payment-pages/new?offering=${data.standard_id}${slugParam}`,
        );
      } else {
        goBackToList();
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create bundle",
        variant: "destructive",
      });
    }
  };

  const isLastStep = current === steps.length - 1;

  return (
    <HasAccess permission="write">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <button
          type="button"
          onClick={goBackToList}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" /> Bundles / New Bundle
        </button>

        <BundleWizardStepper steps={steps} current={current} />

        {/* Prefilled context */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          <span className="text-gray-400">Prefilled:</span>
          <Badge variant="secondary">Product: {productName ?? "—"}</Badge>
          <span className="font-mono text-gray-400">
            standard_id {standardId.slice(0, 8)}… (auto)
          </span>
        </div>

        <Form {...form}>
          {/* Swallow ALL implicit submits (Radix Select/Switch triggers default
              to type=submit, Enter key, etc.). Creation happens only via the
              explicit Create button below, so navigating never creates. */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="space-y-6 rounded-lg border border-gray-200 p-6"
          >
            {current === 0 && <StepBasics form={form} />}
            {isReschedule ? (
              current === 1 && (
                <RescheduleBundlesStep
                  regionId={form.getValues("region_id") ?? ""}
                  productId={productId ?? undefined}
                  currency={form.getValues("currency")}
                  onItemsChange={setRescheduleItems}
                />
              )
            ) : (
              <>
                {current === 1 && <StepPricing form={form} />}
                {current === 2 && (
                  <StepLinks
                    form={form}
                    createPaymentPage={createPaymentPage}
                    setCreatePaymentPage={setCreatePaymentPage}
                    paymentSlug={paymentSlug}
                    setPaymentSlug={setPaymentSlug}
                  />
                )}
                {current === 3 && (
                  <StepReview form={form} productName={productName} />
                )}
              </>
            )}

            <div className="flex justify-between pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={
                  current === 0 ? goBackToList : () => setCurrent((c) => c - 1)
                }
              >
                {current === 0 ? "Cancel" : "← Back"}
              </Button>

              {isLastStep ? (
                isReschedule ? (
                  <Button
                    type="button"
                    disabled={createReschedule.isPending}
                    onClick={handleRescheduleCreate}
                  >
                    {createReschedule.isPending
                      ? "Creating…"
                      : "✓ Create reschedule bundles"}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    disabled={createBundle.isPending}
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {createBundle.isPending ? "Creating…" : "✓ Create Bundle"}
                  </Button>
                )
              ) : (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={checkingSlug}
                >
                  {checkingSlug
                    ? "Checking…"
                    : `Next: ${steps[current + 1].label} →`}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </HasAccess>
  );
}
